import { useMemo } from 'react';

import { Icon } from '@/app/components/icons';
import { IconBuild } from '@/app/components/icons/common-actions/icon-build';
import { IconCalendarMonth } from '@/app/components/icons/ui-actions/icon-calendar-month';
import { type TabItem } from '@/app/components/navigation/tabs';
import { DealerContact } from '@/app/components/shared/dealer/components/shared/dealer-contact';
import { DEALER_SEARCH_KEY } from '@/app/components/shared/dealer/utils/dealer-constant';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import type {
  ContactInfo,
  Dealer,
} from '@/services/contentstack/models/dealer';

interface ServiceItemProps {
  label: string;
  dataTest: string;
}

interface OpeningHoursProps {
  title: string;
  description?: string;
  dataTest: string;
}

const ServiceItem = ({ label, dataTest }: ServiceItemProps) => {
  return (
    <li
      className="text-body-1-fixed text-gray-700"
      {...createDataAttributes({ test: dataTest })}
    >
      {label}
    </li>
  );
};

const OpeningHours = ({ title, description, dataTest }: OpeningHoursProps) => (
  <div className="grid grid-cols-2 items-center gap-x-10">
    <span
      className="text-body-1-fixed font-noto-bold text-gray-700"
      {...createDataAttributes({ test: dataTest })}
    >
      {title}
    </span>
    <span
      className="text-body-1-fixed text-gray-700"
      {...createDataAttributes({ test: `${dataTest}-value` })}
    >
      {description || '-'}
    </span>
  </div>
);

interface ServiceDetailProps {
  selectedTab?: TabItem | null;
  selectedDetailTab: TabItem;
  selectedType?: Dealer['sale' | 'aftersale' | 'bp'] | null;
  dealer: Dealer;
}

export const ServiceDetail = ({
  selectedTab,
  selectedDetailTab,
  selectedType,
  dealer,
}: ServiceDetailProps) => {
  const isTireChanging = useMemo(() => {
    if (selectedDetailTab.value === 'cv') {
      return !!dealer?.aftersale?.tire_changing_cv;
    }

    return !!dealer?.aftersale?.tire_changing_lcv;
  }, [dealer, selectedDetailTab.value]);

  const isOverhaul = useMemo(() => {
    if (selectedDetailTab.value === 'cv') {
      return !!dealer?.aftersale?.overhaul_cv;
    }

    return !!dealer?.aftersale?.overhaul_lcv;
  }, [dealer, selectedDetailTab.value]);

  return (
    <div className="space-y-7">
      {selectedTab?.value === DEALER_SEARCH_KEY.Service && (
        <div className="pt-4">
          <div className="flex items-center gap-x-2">
            <Icon icon={<IconBuild />} className="size-5" />
            <span
              className="text-body-1-fixed font-noto-bold"
              {...createDataAttributes({ test: 'service' })}
            >
              บริการ
            </span>
          </div>
          <ul className="mt-1 list-disc pl-4">
            <ServiceItem label="เช็กตามระยะและซ่อมทั่วไป" dataTest="general" />
            {!!isTireChanging && (
              <ServiceItem label="เปลี่ยนยาง" dataTest="tire-changing" />
            )}
            {!!isOverhaul && (
              <ServiceItem label="โอเวอร์ฮอล์" dataTest="overhaul" />
            )}
          </ul>
        </div>
      )}

      <div
        className={cn('space-y-1.5', {
          'pt-4': selectedTab?.value !== DEALER_SEARCH_KEY.Service,
        })}
      >
        <div className="flex items-center gap-x-2">
          <Icon icon={<IconCalendarMonth />} className="size-5" />
          <span
            className="text-body-1-fixed font-noto-bold"
            {...createDataAttributes({ test: 'open-time' })}
          >
            วันและเวลาทำการ
          </span>
        </div>

        <OpeningHours
          title="จันทร์ - ศุกร์"
          description={selectedType?.open_time?.hour_mon_fri}
          dataTest="mon-fri"
        />
        <OpeningHours
          title="เสาร์"
          description={selectedType?.open_time?.hour_sat}
          dataTest="sat"
        />
        <OpeningHours
          title="อาทิตย์"
          description={selectedType?.open_time?.hour_sun}
          dataTest="sun"
        />
      </div>

      {!!selectedType?.open_time?.remark && (
        <span
          className="text-body-2-fixed text-gray-600"
          {...createDataAttributes({ test: 'open-time-remark' })}
        >
          *{selectedType.open_time.remark}
        </span>
      )}

      <DealerContact
        data={selectedType?.contact as ContactInfo}
        selectedVehicle={selectedDetailTab?.value}
      />
    </div>
  );
};
